/**
 * Authenticated app shell.
 *
 *   ┌──────────┬─────────────────────╔═══════════════════╗
 *   │          │ [Toggle] · breadcrumb  ☀ Suggestions ⤢ ✕  │
 *   │          ├─────────────────────╠═══════════════════╣
 *   │ Sidebar  │ Canvas page         ║ Resizable chat    ║
 *   │          │ (scrolls)           ║ (drag to resize)  ║
 *   │          │                     ║                   ║
 *   │          │                     ╚═══════════════════╝
 *   └──────────┴─────────────────────────────────────────┘
 *
 * At wide widths the right panel is a real ResizablePanelGroup — the user
 * can drag the splitter to widen/narrow the chat (within sane bounds), or
 * hit ⤢ to have it cover the whole canvas area for a focused chat session.
 *
 * Below a layout threshold (workspace area < ~1100px) the chat switches to
 * an *overlay* sheet: the canvas stays full-width and the chat slides over
 * the right edge. This avoids the squeeze we'd get from a side-by-side
 * split when there isn't enough horizontal room to do both surfaces
 * justice. On mobile the chat panel becomes a regular Sheet trigger.
 *
 * Tauri / desktop: the top chrome doubles as the window drag region and
 * leaves room for the macOS traffic-light buttons (via the
 * `--titlebar-pad-l` CSS variable set by the desktop entrypoint).
 */

import { useQuery } from "@tanstack/react-query";
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import { MessageSquareIcon, PanelRightCloseIcon, PanelRightIcon } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import type { RawOperation } from "@/components/chat/humanize-changes";
import { proposalDisplayTitle } from "@/lib/proposal-title";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Spinner } from "@/components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { cn } from "@/lib/utils";
import { useApi } from "../api";
import { getArtifact } from "../api/artifacts";
import { getObjectiveBySlug, getProjectBySlug, getWorkPackage } from "@/api/plan";
import { useAuth } from "../auth";
import { ArtifactIcon } from "../components/artifact/ArtifactIcon";
import { ChatPanel } from "../components/chat/ChatPanel";
import { UnifiedRail } from "../components/chat/UnifiedRail";
import { canComment, canEdit, railModeForRole } from "../components/chat/railMode";
import { AppSidebar } from "../components/layout/AppSidebar";
import { OrgAgentPane } from "../components/layout/OrgAgentPane";
import { WorkspaceAgentPane } from "../components/layout/WorkspaceAgentPane";
import { PlanAgentPane } from "../components/plan/PlanAgentPane";
import { usePlanMode } from "../components/plan/usePlanMode";
import { SuggestionsBadge } from "../components/suggestion/SuggestionsBadge";
import { useIsMobile } from "../hooks/use-mobile";
import { DemoFeedbackChrome, DemoWelcomeChrome } from "../components/demo/DemoChrome";
import { ActiveArtifactProvider, useActiveArtifact } from "../lib/active-artifact";
import { ActiveThreadProvider, useActiveThread } from "../lib/active-thread";
import { onAgentPanelRequest } from "../lib/composerBus";
import {
  CHAT_PANEL_MAX_PCT,
  CHAT_PANEL_MIN_PCT,
  ChatPanelProvider,
  useChatPanel,
} from "../lib/chat-panel";

export const Route = createFileRoute("/_app")({
  component: AppLayout,
});

/**
 * Below this width the chat panel switches from a split to an overlay
 * sheet. Empirically: with the 256px sidebar visible, a 1100px window
 * leaves ~844px for the workspace area — once both canvas and chat
 * compete for that, neither fits comfortably. Slide-over wins.
 */
const CHAT_OVERLAY_BREAKPOINT_PX = 1100;

/**
 * Below this width the Plan / workspace / org agent rail (RailLayout) stops
 * sharing the row and slides in as an overlay Sheet (opened by a FAB), matching
 * the mockup's tablet/mobile rail-sheet. 1024 covers both tablet and phone.
 */
const RAIL_OVERLAY_BREAKPOINT_PX = 1024;

function useMatchesMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

function AppLayout() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Auth redirects run in an EFFECT, never as a render-time <Navigate>.
  // Rendering <Navigate> from a layout that stays mounted while the auth
  // query settles re-schedules the navigation on every re-render; with a
  // real-network /me (any latency at all) that amplified into an unbounded
  // update loop that OOM-crashed the tab for signed-out visitors hitting
  // guarded routes directly.
  const redirectTo = auth.isLoading
    ? null
    : !auth.me
      ? "/signin"
      : auth.me.user.artifact_only
        ? // Opens the pages shared with it and nothing else: the API refuses
          // every call this shell would make, so don't render it at all.
          "/shared"
        : auth.me.memberships.length === 0 && !location.pathname.startsWith("/onboarding")
          ? "/onboarding"
          : null;
  useEffect(() => {
    if (redirectTo) void navigate({ to: redirectTo, replace: true });
  }, [redirectTo, navigate]);

  if (auth.isLoading || redirectTo) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Spinner className="text-[var(--color-brand-ochre)]" />
      </div>
    );
  }

  return (
    <ActiveThreadProvider>
      <ActiveArtifactProvider>
        <ChatPanelProvider>
          <SidebarProvider
            className="h-dvh overflow-hidden"
            // Collapsed sidebar = a 60px ICON RAIL (mockup), not shadcn's 3rem default.
            style={{ "--sidebar-width-icon": "60px" } as React.CSSProperties}
          >
            <AppSidebar />
            <SidebarInset className="overflow-hidden">
              {/* Demo sessions only: the personalized welcome banner, pinned
                  above the topbar + content (SidebarInset is a flex column,
                  so AppContent's flex-1 keeps filling the rest). Renders
                  nothing unless GET /me carried a demo.welcome message. */}
              <DemoWelcomeChrome />
              <AppContent />
            </SidebarInset>
            {/* Demo-session-only chrome: the floating feedback widget (its
                placement reads this shell's chat-panel/active-thread
                providers). The guided tour mounts in routes/__root.tsx so it
                survives the `_app` ⇄ /manage layout swap mid-walkthrough.
                Renders nothing unless GET /me carried a `demo` block. */}
            <DemoFeedbackChrome />
          </SidebarProvider>
        </ChatPanelProvider>
      </ActiveArtifactProvider>
    </ActiveThreadProvider>
  );
}

function AppContent() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const isNarrow = useMatchesMedia(`(max-width: ${CHAT_OVERLAY_BREAKPOINT_PX - 1}px)`);
  const { threadId, canvasId, workspaceId, chatHeaderSlot, draft, onDraftPromoted, onStartDraft } =
    useActiveThread();
  const {
    open,
    setOpen,
    setAvailable,
    maximised,
    toggleMaximised,
    sizePct,
    setSizePct,
    setMobileOpen,
  } = useChatPanel();
  const planMode = usePlanMode();
  const api = useApi();

  // "Open the agent panel" (lib/composerBus.ts `requestAgentPanel`) — the demo
  // rail's ask beat hands its question to the assistant at ANY width: the
  // docked rail or the narrow-window sheet (`open`) and the phone sheet
  // (`mobileOpen`). Before this, below ~768px there was no panel for the
  // question to land in and the rail's Send did nothing (Oxera, filmed).
  useEffect(
    () =>
      onAgentPanelRequest(() => {
        setOpen(true);
        // Same breakpoint as useIsMobile: only a phone has the sheet.
        if (window.innerWidth < 768) setMobileOpen(true);
      }),
    [setOpen, setMobileOpen],
  );

  // Right-rail access gate. The active artifact (if any) takes precedence over
  // the canvas — its Comments tab must show the ARTIFACT's comments, gated on the
  // ARTIFACT's role. Otherwise we gate on the canvas's role. Both `my_role`s ride
  // the entity detail payloads (Part A). Queries are cache-shared with the rest
  // of the app and only enabled when there's something to resolve.
  const { active: activeArtifact } = useActiveArtifact();
  const canvasRoleQuery = useQuery({
    queryKey: ["canvas", canvasId],
    queryFn: () => (canvasId ? api.getCanvas(canvasId) : Promise.resolve(null)),
    enabled: !!canvasId,
    select: (p) => p?.my_role ?? null,
  });
  const artifactRoleQuery = useQuery({
    queryKey: ["artifact", activeArtifact?.artifactId],
    queryFn: () =>
      activeArtifact ? getArtifact(activeArtifact.artifactId) : Promise.resolve(null),
    enabled: !!activeArtifact,
    select: (a) => a?.my_role ?? null,
  });
  // Resolve the rail's mode + comment target for the canvas/artifact split.
  const railRole = activeArtifact ? artifactRoleQuery.data : canvasRoleQuery.data;
  const railMode = railModeForRole(railRole);
  // Same role feeds the comment-write gate: viewer → read-only composer hidden,
  // commenter+ → composer shown.
  const railCanComment = canComment(railRole);
  // editor+ may resolve/reopen comment threads on the canvas/artifact.
  const railCanResolve = canEdit(railRole);
  const railCommentTargetKind = activeArtifact ? ("artifact" as const) : ("canvas" as const);
  const railCommentTargetId = activeArtifact ? activeArtifact.artifactId : (canvasId ?? null);

  // Show the chat panel whenever there's a live thread OR we're in
  // draft mode (no thread yet, but the user has explicitly asked to
  // start a new conversation — the composer is enabled so they can
  // type the first message).
  const showChat = !isMobile && (!!threadId || !!draft);

  useEffect(() => {
    setAvailable(showChat);
  }, [showChat, setAvailable]);

  // Maximize means the chat panel takes 100% of the workspace area; restore
  // collapses back to its remembered sizePct.
  const isMaximised = showChat && open && maximised;

  // Below the narrow breakpoint, the chat panel slides over the canvas
  // as a Sheet (the canvas stays full-width). This avoids both surfaces
  // being squeezed when the workspace area is too small for a sensible
  // split. We render the sheet alongside the canvas — the canvas never
  // unmounts (preserves ActiveThread side-effects, same constraint as
  // the maximize path).
  if (isNarrow && showChat) {
    return (
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col">
          <TopChrome />
          <div className="min-h-0 flex-1 overflow-hidden">
            <Outlet />
          </div>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="right"
            className="flex w-full max-w-[480px] flex-col gap-0 bg-background p-0 sm:max-w-[480px]"
            showCloseButton={false}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Assistant</SheetTitle>
              <SheetDescription>Conversation with the canvas assistant.</SheetDescription>
            </SheetHeader>
            {/* No maximise affordance — the panel is already an overlay,
                so "make it bigger" has no meaningful target. Dropping the
                button removes the icon noise next to the close. */}
            <ChatPanel
              threadId={threadId}
              canvasId={canvasId}
              workspaceId={workspaceId}
              draft={draft}
              onDraftPromoted={onDraftPromoted}
              onStartDraft={onStartDraft}
              headerCenterSlot={chatHeaderSlot}
            />
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // CRITICAL: <Outlet /> stays mounted continuously regardless of the chat
  // panel's open / maximised state. The route component (e.g. CanvasPage)
  // owns ActiveThread context state via setActiveThread; if we ever
  // unmount it (even just to swap layouts), its cleanup clears the thread,
  // which flips `isMaximised` back to false here, which remounts the route,
  // which sets the thread again, … "Maximum update depth exceeded".
  //
  // Layout strategy:
  //   - Narrow + chat available → overlay sheet (handled above).
  //   - No chat available  → just render the canvas + topbar full width.
  //   - Chat closed        → same as above (the toggle button lives in
  //                          the topbar so the user can reopen it).
  //   - Chat open, split   → ResizablePanelGroup with both panes.
  //   - Chat open, max     → canvas pane is hidden (display:none) but
  //                          stays in the React tree; chat takes 100%.
  //                          We deliberately bypass the resizable group
  //                          because react-resizable-panels v4 ping-pongs
  //                          layout state when reduced to a single panel.

  // Plan mode: a full-height agent rail sits beside [TopChrome + content], so
  // its header aligns with the top bar (mirrors Act's chat panel and the
  // mockup's `.wpv` row). Plan never enables the chat panel, so this branch
  // must come before the chat-dependent branches below.
  if (planMode === "plan") {
    return <RailLayout rail={<PlanAgentPane />} />;
  }

  // Act-mode workspaces directory (`/workspaces`): the org-wide overview, with
  // no single workspace to scope to — mount the read-only organization analyst
  // (surface "org") so the user can ask across every workspace/project/objective.
  const onWorkspacesList = /^\/workspaces\/?$/.test(location.pathname);
  if (onWorkspacesList) {
    return <RailLayout rail={<OrgAgentPane />} />;
  }

  // Act-mode workspace overview (`/w/{slug}`): like Plan, a full-height agent
  // rail sits beside [TopChrome + content] (mirrors the mockup's WorkShell, where
  // the workspace overview keeps the 3-panel shell). The canvas chat is
  // canvas-scoped, so here we show the static Workspace Agent instead.
  const onWorkspaceHome = /^\/w\/[^/]+\/?$/.test(location.pathname);
  if (onWorkspaceHome) {
    return <RailLayout rail={<WorkspaceAgentPane />} />;
  }

  if (!showChat || !open) {
    return (
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col">
          <TopChrome />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </div>
        </div>
      </div>
    );
  }

  if (isMaximised) {
    return (
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Hidden canvas pane keeps the route component (and its
            ActiveThread side-effects) mounted, preventing the
            unmount/remount loop described above. */}
        <div className="hidden">
          <Outlet />
        </div>
        <div className="flex min-w-0 flex-1 flex-col bg-background">
          <ChatPanel
            threadId={threadId}
            canvasId={canvasId}
            workspaceId={workspaceId}
            draft={draft}
            onDraftPromoted={onDraftPromoted}
            onStartDraft={onStartDraft}
            maximised
            onToggleMaximised={toggleMaximised}
            headerCenterSlot={chatHeaderSlot}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      {/*
        NOTE: react-resizable-panels v4 treats a *numeric* defaultSize /
        minSize / maxSize as PIXELS (a breaking change from v3, which
        treated bare numbers as percentages). All sizing props below
        therefore use the `"30%"` string form on purpose.
      */}
      <ResizablePanelGroup orientation="horizontal" className="min-h-0 flex-1">
        <ResizablePanel
          id="canvas"
          defaultSize={`${100 - sizePct}%`}
          minSize="20%"
          className="flex min-w-0 flex-col"
        >
          <TopChrome />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </div>
        </ResizablePanel>
        {/* Invisible drag strip: no visible bar/grip, so the rail panel's own
            `border-l border-border` + outward shadow is the SINGLE edge — a clean
            mirror of the left sidebar's `border-r` edge (which has no grip/handle).
            Resize still works via the handle's transparent hit-area + col-resize
            cursor on hover. */}
        <ResizableHandle className="w-0 border-0 bg-transparent" />
        <ResizablePanel
          id="chat"
          defaultSize={`${sizePct}%`}
          minSize={`${CHAT_PANEL_MIN_PCT}%`}
          maxSize={`${CHAT_PANEL_MAX_PCT}%`}
          onResize={(size) => {
            // Guard: react-resizable-panels v4 calls onResize on every
            // layout pass (mount, parent resize, etc.). Without a
            // tolerance check we'd thrash setState → re-render → resize
            // → setState forever and React bails out with "Maximum
            // update depth exceeded".
            if (Math.abs(size.asPercentage - sizePct) < 0.5) return;
            setSizePct(size.asPercentage);
          }}
          className={cn(
            "flex min-w-0 flex-col border-l border-border bg-background",
          )}
        >
          {/* Unified rail: Agent / Comments (per-entity role-gated). When an
              artifact is active it owns the Comments tab (artifact's comments +
              role); otherwise the canvas does. The wrapper carries the demo
              tour's [data-tour=agent] anchor (same as RailLayout's rail). */}
          <div data-tour="agent" className="flex min-h-0 flex-1 flex-col">
            <UnifiedRail
              mode={railMode}
              canComment={railCanComment}
              canResolve={railCanResolve}
              commentTargetKind={railCommentTargetKind}
              commentTargetId={railCommentTargetId}
              threadId={threadId}
              canvasId={canvasId}
              workspaceId={workspaceId}
              draft={draft}
              onDraftPromoted={onDraftPromoted}
              onStartDraft={onStartDraft}
              maximised={false}
              onToggleMaximised={toggleMaximised}
              headerCenterSlot={chatHeaderSlot}
            />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

/**
 * RailLayout — [TopChrome + Outlet] beside a draggable agent rail, using the
 * SAME resizable split + remembered width (sizePct) as the canvas/artifact chat,
 * so the agent rail is draggable and consistent on every page (Plan + workspace
 * overview). The rail's panel owns the width/border/bg; the passed `rail`
 * component just fills it.
 */
function RailLayout({ rail }: { rail: ReactNode }) {
  const { sizePct, setSizePct } = useChatPanel();
  // Below the compact breakpoint the rail can't share the row without cramping
  // (verified: it squeezed to ~120px with badly-wrapping text). Match the mockup's
  // tablet/mobile behaviour: render the page full-width and slide the rail in as an
  // overlay Sheet opened by a floating button — the same narrow-overlay treatment
  // the canvas chat uses. ≤1023px covers both tablet and phone.
  const isNarrow = useMatchesMedia(`(max-width: ${RAIL_OVERLAY_BREAKPOINT_PX - 1}px)`);
  const [sheetOpen, setSheetOpen] = useState(false);

  if (isNarrow) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <TopChrome />
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet />
          <Button
            onClick={() => setSheetOpen(true)}
            aria-label="Open assistant"
            className="absolute right-4 bottom-4 z-20 size-12 rounded-full shadow-lg"
            size="icon"
          >
            <MessageSquareIcon />
          </Button>
        </div>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent
            side="right"
            className="flex w-[92vw] flex-col gap-0 bg-background p-0 sm:max-w-[420px]"
            showCloseButton={false}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Assistant</SheetTitle>
              <SheetDescription>Agent and comments for this page.</SheetDescription>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{rail}</div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <ResizablePanelGroup orientation="horizontal" className="min-h-0 flex-1">
        <ResizablePanel
          id="content"
          defaultSize={`${100 - sizePct}%`}
          minSize="20%"
          className="flex min-w-0 flex-col"
        >
          <TopChrome />
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </div>
        </ResizablePanel>
        {/* Invisible drag strip: no visible bar/grip, so the rail panel's own
            `border-l border-border` + outward shadow is the SINGLE edge — a clean
            mirror of the left sidebar's `border-r` edge (which has no grip/handle).
            Resize still works via the handle's transparent hit-area + col-resize
            cursor on hover. */}
        <ResizableHandle className="w-0 border-0 bg-transparent" />
        <ResizablePanel
          id="rail"
          defaultSize={`${sizePct}%`}
          minSize={`${CHAT_PANEL_MIN_PCT}%`}
          maxSize={`${CHAT_PANEL_MAX_PCT}%`}
          onResize={(size) => {
            if (Math.abs(size.asPercentage - sizePct) < 0.5) return;
            setSizePct(size.asPercentage);
          }}
          className="flex min-w-0 flex-col border-l border-border bg-background"
        >
          {/* data-tour anchor: the demo guided tour spotlights the agent rail. */}
          <div data-tour="agent" className="flex min-h-0 flex-1 flex-col">
            {rail}
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

function TopChrome() {
  return (
    <header
      data-tauri-drag-region
      // Leaves room for macOS traffic lights when running in Tauri with
      // `titleBarStyle: Overlay`. Falls back to the standard 0.75rem
      // (px-3 equivalent) on web — see `--titlebar-pad-l` in styles.css.
      style={{
        paddingLeft: "max(0.75rem, var(--titlebar-pad-l))",
        background: "var(--color-topbar)",
      }}
      // Distinct chrome band: lighter than the canvas-surround background,
      // darker than the canvas paper. Sits between the two so the breadcrumb
      // band has its own identity instead of bleeding into either side.
      className="flex h-12 shrink-0 items-center gap-2 border-b border-border pr-3"
    >
      {/* Mobile only: opens the off-canvas Sheet. On desktop the sidebar is
          collapsed/expanded by the edge chevron (AppSidebar .edge-toggle). */}
      <SidebarTrigger className="md:hidden" />
      <Separator orientation="vertical" className="h-6 shrink-0 md:hidden" />
      {/* Mono-uppercase tracked breadcrumb, matching the mockup's `.topbar-crumb`
          (font-family/size/transform/letter-spacing inherit to every segment;
          shadcn's BreadcrumbList/Page already supply the faint→ink color tiers). */}
      <div className="min-w-0 flex-1 overflow-hidden font-mono text-[11px] uppercase tracking-[0.06em]">
        <CrumbStrip />
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        <SuggestionsBadge />
        <ChatToggleButton />
        <MobileChatTrigger />
      </div>
    </header>
  );
}

function CrumbStrip() {
  const location = useLocation();
  const params = useParams({ strict: false }) as {
    slug?: string;
    canvasId?: string;
    proposalId?: string;
    artifactId?: string;
  };
  const api = useApi();
  const searchCanvasId = useMemo(() => {
    const raw = location.search as string | Record<string, unknown> | undefined;
    if (typeof raw === "string") {
      const qs = raw.startsWith("?") ? raw.slice(1) : raw;
      return new URLSearchParams(qs).get("canvasId") ?? undefined;
    }
    if (raw && typeof raw === "object" && "canvasId" in raw) {
      const v = (raw as { canvasId?: unknown }).canvasId;
      return typeof v === "string" ? v : undefined;
    }
    return undefined;
  }, [location.search]);

  // The proposal preview route doesn't have `slug` / `canvasId` in its path,
  // but `/v/$proposalId?canvasId=` carries the canvas; active-thread context
  // fills in once SuggestionPreview loads.
  const active = useActiveThread();
  const canvasId = params.canvasId ?? active.canvasId ?? searchCanvasId ?? undefined;
  const workspaceId = active.workspaceId ?? undefined;

  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => api.listWorkspaces(),
  });
  // Resolve workspace by slug (when on /w/$slug/c/$canvasId) or by id
  // (when on /v/$proposalId, where slug isn't part of the URL).
  const workspace =
    workspaces.data?.find((w) => params.slug && w.slug === params.slug) ??
    workspaces.data?.find((w) => workspaceId && w.id === workspaceId);

  const canvas = useQuery({
    queryKey: ["canvas", canvasId, "name"],
    queryFn: () => (canvasId ? api.getCanvas(canvasId) : Promise.resolve(null)),
    enabled: !!canvasId,
    select: (payload) => payload?.canvas ?? null,
  });

  const onProposal = Boolean(params.proposalId);
  const onArtifact = Boolean(params.artifactId);

  const seg = location.pathname.split("/");

  // Artifact crumb data — only fetched when the route has an artifactId.
  // Returns null otherwise so the crumb renders nothing.
  const artifactQuery = useQuery({
    queryKey: ["artifact-crumb", params.artifactId],
    queryFn: () =>
      params.artifactId ? getArtifact(params.artifactId) : Promise.resolve(null),
    enabled: onArtifact,
  });

  const proposalTitleQuery = useQuery({
    queryKey: ["suggestion-preview", canvasId, params.proposalId],
    queryFn: () => api.previewProposal(canvasId!, params.proposalId!),
    enabled: Boolean(onProposal && canvasId && params.proposalId),
    select: (d) =>
      proposalDisplayTitle(
        { reasoning: d.proposal.reasoning },
        d.proposal.operations as RawOperation[],
      ),
  });

  const proposalCrumbLabel = proposalTitleQuery.data;

  // Plan-mode leaf refs (KR-N / WP-N / T-N) for the breadcrumb — derived from the
  // parent entity's child order. Reuses the same warm caches the pages populate.
  const planKind = seg[1] === "plan" ? seg[2] : undefined;
  const planObjSlug = planKind === "o" ? seg[3] : undefined;
  const planProjSlug = planKind === "p" ? seg[3] : undefined;
  const onKrCrumb = planKind === "o" && seg[4] === "kr";
  const onWpCrumb = planKind === "p" && seg[4] === "wp";
  const onTaskCrumb = onWpCrumb && seg[6] === "t";
  const planObjQuery = useQuery({
    queryKey: ["plan", "objective-by-slug", planObjSlug],
    queryFn: () => getObjectiveBySlug(planObjSlug!),
    enabled: !!planObjSlug && onKrCrumb,
  });
  const planProjQuery = useQuery({
    queryKey: ["plan", "project-by-slug", planProjSlug],
    queryFn: () => getProjectBySlug(planProjSlug!),
    enabled: !!planProjSlug && onWpCrumb,
  });
  const planWpQuery = useQuery({
    queryKey: ["plan", "wp", onTaskCrumb ? seg[5] : undefined],
    queryFn: () => getWorkPackage(seg[5]!),
    enabled: onTaskCrumb && !!seg[5],
  });

  // ── Plan-mode breadcrumb (workspace-independent `/plan/*` routes) ──────────
  // Roots at the ENTITY TYPE we're viewing — `Workspaces` / `Projects` /
  // `Objectives` (or just `All work`) — so the crumb says what you're looking at,
  // not a generic "Plan". Then › <entity name> › [sub].
  if (seg[1] === "plan") {
    const kind = seg[2]; // all-work | w | p | o
    const humanize = (s = "") =>
      s.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    const rootLabel =
      kind === "w"
        ? "Workspaces"
        : kind === "p"
          ? "Projects"
          : kind === "o"
            ? "Objectives"
            : "All work";

    // All work is the org-wide rollup itself — a single terminal crumb.
    if (kind === "all-work") {
      return (
        <Breadcrumb className="min-w-0">
          <BreadcrumbList className="min-w-0 flex-nowrap overflow-hidden">
            <BreadcrumbItem className="min-w-0 shrink">
              <BreadcrumbPage className="truncate font-medium">All work</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      );
    }

    const entityLabel =
      kind === "w"
        ? (workspaces.data?.find((w) => w.slug === seg[3])?.name ?? humanize(seg[3]))
        : humanize(seg[3]);
    // Leaf crumb: the entity's ref (KR-N / WP-N / T-N), resolved from the parent's
    // child order; falls back to the generic label until the query resolves.
    let sub: string | null = null;
    if (onTaskCrumb) {
      const tasks = planWpQuery.data?.outcomes.flatMap((o) => o.tasks) ?? [];
      const i = tasks.findIndex((t) => t.id === seg[7]);
      sub = i >= 0 ? `T-${i + 1}` : "Task";
    } else if (onWpCrumb) {
      const wps = planProjQuery.data?.work_packages ?? [];
      const i = wps.findIndex((w) => w.work_package.id === seg[5]);
      sub = i >= 0 ? `WP-${i + 1}` : "Work package";
    } else if (onKrCrumb) {
      const krs = planObjQuery.data?.key_results ?? [];
      const i = krs.findIndex((k) => k.id === seg[5]);
      sub = i >= 0 ? `KR-${i + 1}` : "Key result";
    }
    // A task lives under a work package, so its crumb keeps the WP-N level (linked):
    // Projects › <project> › WP-N › T-N.
    let taskWpRef: string | null = null;
    if (onTaskCrumb) {
      const wps = planProjQuery.data?.work_packages ?? [];
      const i = wps.findIndex((w) => w.work_package.id === seg[5]);
      taskWpRef = i >= 0 ? `WP-${i + 1}` : "Work package";
    }
    return (
      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="min-w-0 flex-nowrap overflow-hidden">
          <BreadcrumbItem className="shrink-0">
            <BreadcrumbLink className="hover:text-foreground" render={<Link to="/plan/all-work" />}>
              {rootLabel}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="shrink-0" />
          <BreadcrumbItem className="min-w-0 shrink">
            {sub ? (
              <span className="truncate font-medium text-muted-foreground">{entityLabel}</span>
            ) : (
              <BreadcrumbPage className="truncate font-medium">{entityLabel}</BreadcrumbPage>
            )}
          </BreadcrumbItem>
          {onTaskCrumb && taskWpRef && (
            <>
              <BreadcrumbSeparator className="shrink-0" />
              <BreadcrumbItem className="shrink-0">
                <BreadcrumbLink
                  className="hover:text-foreground"
                  render={
                    <Link
                      to="/plan/p/$projectSlug/wp/$wpId"
                      params={{ projectSlug: seg[3]!, wpId: seg[5]! }}
                    />
                  }
                >
                  {taskWpRef}
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}
          {sub && (
            <>
              <BreadcrumbSeparator className="shrink-0" />
              <BreadcrumbItem className="min-w-0 shrink">
                <BreadcrumbPage className="truncate font-medium">{sub}</BreadcrumbPage>
              </BreadcrumbItem>
            </>
          )}
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  // Empty state — keep silent. The breadcrumb is purely contextual; an
  // explicit "Loading…" chip just adds noise on every cold open.
  if (!workspace && !canvas.data) {
    return <div className="min-w-0 flex-1" />;
  }

  // Responsive collapse: at < md (768px) and the route has 3+ levels
  // (workspace → canvas → proposal), the middle workspace crumb hides
  // behind a `…` ellipsis to keep the canvas + proposal labels visible.
  // The terminal crumb always truncates to fit; middle crumbs never wrap.
  const onCanvas = Boolean(canvas.data);
  const showWorkspaceCrumb = Boolean(workspace);
  const collapseWorkspace = showWorkspaceCrumb && onCanvas;

  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="min-w-0 flex-nowrap overflow-hidden">
        {/* "Workspaces" label — hidden below md so narrow widths spend
            their horizontal budget on the meaningful crumbs. */}
        <BreadcrumbItem className="hidden shrink-0 md:inline-flex">
          <BreadcrumbLink
            className="hover:text-foreground"
            render={<Link to="/workspaces" />}
          >
            Workspaces
          </BreadcrumbLink>
        </BreadcrumbItem>

        {showWorkspaceCrumb && (
          <>
            <BreadcrumbSeparator className="hidden shrink-0 md:inline-flex" />
            {/* Below md, when we have deeper crumbs, collapse this to "…" */}
            {collapseWorkspace ? (
              <>
                <BreadcrumbItem className="shrink-0 md:hidden">
                  <BreadcrumbLink
                    className="inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    aria-label={`Back to ${workspace?.name ?? "workspace"}`}
                    render={<Link to="/w/$slug" params={{ slug: workspace!.slug }} />}
                  >
                    <span aria-hidden="true">…</span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbItem className="hidden shrink-0 md:inline-flex">
                  <BreadcrumbLink
                    className="inline-flex items-center gap-1.5 font-medium"
                    render={<Link to="/w/$slug" params={{ slug: workspace!.slug }} />}
                  >
                    <span className="max-w-[160px] truncate">{workspace!.name}</span>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            ) : (
              <BreadcrumbItem className="min-w-0 shrink">
                <BreadcrumbLink
                  className="inline-flex items-center gap-1.5 font-medium"
                  render={<Link to="/w/$slug" params={{ slug: workspace!.slug }} />}
                >
                  <span className="truncate">{workspace!.name}</span>
                </BreadcrumbLink>
              </BreadcrumbItem>
            )}
          </>
        )}

        {onCanvas && canvas.data && (
          <>
            <BreadcrumbSeparator className="shrink-0" />
            <BreadcrumbItem
              className={cn(
                "min-w-0",
                onProposal ? "shrink hidden sm:inline-flex" : "max-w-full shrink",
              )}
            >
              {onProposal || onArtifact ? (
                <BreadcrumbLink
                  className="inline-flex min-w-0 items-center gap-1.5 font-medium"
                  render={
                    <Link
                      to="/w/$slug/c/$canvasId"
                      params={{ slug: workspace?.slug ?? "", canvasId: canvas.data.id }}
                    />
                  }
                >
                  <span className="truncate">{canvas.data.name}</span>
                </BreadcrumbLink>
              ) : (
                <BreadcrumbPage className="inline-flex min-w-0 items-center gap-1.5 font-medium">
                  <span className="truncate">{canvas.data.name}</span>
                </BreadcrumbPage>
              )}
            </BreadcrumbItem>
          </>
        )}

        {/* Artifact crumb — appended below the canvas crumb when the
            user is on `/w/{slug}/c/{cid}/a/{aid}`. Carries a small
            file-type glyph so the format reads at a glance. */}
        {onArtifact && canvas.data && (
          <>
            <BreadcrumbSeparator className="shrink-0" />
            <BreadcrumbItem className="min-w-0 max-w-full flex-1 shrink">
              <BreadcrumbPage
                className="inline-flex min-w-0 items-center gap-1.5 font-medium"
                title={artifactQuery.data?.artifact?.title}
              >
                {artifactQuery.data?.artifact?.format && (
                  <ArtifactIcon
                    format={artifactQuery.data.artifact.format}
                    className="size-3.5"
                  />
                )}
                <span className="truncate">
                  {artifactQuery.data?.artifact?.title ?? "Artifact"}
                </span>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}

        {onProposal && canvas.data && (
          <>
            <BreadcrumbSeparator className="shrink-0" />
            <BreadcrumbItem className="min-w-0 max-w-full flex-1 shrink">
              <BreadcrumbPage
                className="block truncate font-medium text-foreground/80"
                title={proposalCrumbLabel || undefined}
              >
                {proposalCrumbLabel ?? "Suggested version"}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ChatToggleButton() {
  const isMobile = useIsMobile();
  const { available, open, toggle } = useChatPanel();
  if (isMobile || !available) return null;
  const Icon = open ? PanelRightCloseIcon : PanelRightIcon;
  const label = open ? "Hide assistant" : "Show assistant";
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={toggle}
            aria-label={label}
            aria-pressed={open}
            data-tour="agent-toggle"
          />
        }
      >
        <Icon />
      </TooltipTrigger>
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  );
}

function MobileChatTrigger() {
  const isMobile = useIsMobile();
  const { threadId, canvasId, workspaceId, chatHeaderSlot, draft, onDraftPromoted, onStartDraft } =
    useActiveThread();
  // Controlled from the chat-panel context, so `requestAgentPanel` can open
  // it from outside (the demo rail's ask beat on a phone).
  const { mobileOpen, setMobileOpen } = useChatPanel();
  // A draft — the canvas route's new conversation — is a conversation too:
  // the composer is live in it. Requiring a thread hid the panel there.
  if (!isMobile || (!threadId && !draft)) return null;

  return (
    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
      <SheetTrigger render={<Button variant="ghost" size="icon-sm" aria-label="Open assistant" />}>
        <MessageSquareIcon />
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[calc(100%-3rem)] bg-sidebar p-0 text-sidebar-foreground sm:max-w-(--chat-sidebar-width)"
        showCloseButton={false}
      >
        <SheetHeader className="sr-only">
          <SheetTitle>Assistant</SheetTitle>
          <SheetDescription>Conversation with the canvas assistant.</SheetDescription>
        </SheetHeader>
        <ChatPanel
          threadId={threadId}
          canvasId={canvasId}
          workspaceId={workspaceId}
          draft={draft}
          onDraftPromoted={onDraftPromoted}
          onStartDraft={onStartDraft}
          headerCenterSlot={chatHeaderSlot}
        />
      </SheetContent>
    </Sheet>
  );
}
